import React, { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton
} from '@mui/material'
import {
  PlayArrow,
  Download,
  Schedule,
  Delete,
  Visibility,
  GetApp
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import PageHeader from '../components/ui/PageHeader'
import reportingService from '../services/reportingService'
import type {
  SalesReportRequest,
  InventoryReportRequest,
  PricingReportRequest,
  FinancialReportRequest,
  ReportJobStatus
} from '../services/reportingService'

interface ReportJob {
  id: string
  type: string
  name: string
  status: 'pending' | 'in_progress' | 'completed' | 'failed'
  progress: number
  createdAt: string
  completedAt?: string
  downloadUrl?: string
  errorMessage?: string
}

const ReportGeneratorMUI: React.FC = () => {
  const [reportType, setReportType] = useState<'sales' | 'inventory' | 'pricing' | 'financial'>('sales')
  const [startDate, setStartDate] = useState<Date | null>(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [format, setFormat] = useState<'json' | 'csv' | 'pdf'>('csv')
  const [loading, setLoading] = useState(false)
  const [reportJobs, setReportJobs] = useState<ReportJob[]>([])
  const [jobStatusDialog, setJobStatusDialog] = useState<string | null>(null)
  const [jobStatus, setJobStatus] = useState<ReportJobStatus | null>(null)

  // Sales report specific fields
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day')
  const [customerId, setCustomerId] = useState('')
  const [customerType, setCustomerType] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [includeCancelled, setIncludeCancelled] = useState(false)

  // Inventory report specific fields
  const [warehouseId, setWarehouseId] = useState('')
  const [lowStockThreshold, setLowStockThreshold] = useState<number | ''>('')
  const [includeInactive, setIncludeInactive] = useState(false)

  // Pricing report specific fields
  const [tierId, setTierId] = useState('')
  const [productId, setProductId] = useState('')
  const [includeOverrides, setIncludeOverrides] = useState(true)

  // Financial report specific fields
  const [financialReportType, setFinancialReportType] = useState<'unpaid_invoices' | 'payment_reconciliation' | 'revenue_analysis'>('unpaid_invoices')
  const [paymentGateway, setPaymentGateway] = useState('')

  useEffect(() => {
    loadReportJobs()
  }, [])

  const loadReportJobs = () => {
    // Mock report jobs - in real implementation, this would come from an API
    const mockJobs: ReportJob[] = [
      {
        id: '1',
        type: 'sales',
        name: 'Sales Report - Last 30 Days',
        status: 'completed',
        progress: 100,
        createdAt: new Date(Date.now() - 3600000).toISOString(),
        completedAt: new Date(Date.now() - 3000000).toISOString(),
        downloadUrl: `${import.meta.env.VITE_API_BASE_URL || ''}/api/reports/download/1`
      },
      {
        id: '2',
        type: 'inventory',
        name: 'Inventory Report - Current Stock',
        status: 'in_progress',
        progress: 65,
        createdAt: new Date(Date.now() - 1800000).toISOString()
      },
      {
        id: '3',
        type: 'financial',
        name: 'Unpaid Invoices Report',
        status: 'failed',
        progress: 0,
        createdAt: new Date(Date.now() - 7200000).toISOString(),
        errorMessage: 'Database connection timeout'
      }
    ]
    setReportJobs(mockJobs)
  }

  const generateReport = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates')
      return
    }

    setLoading(true)
    try {
      const dateRange = {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0]
      }

      let result: any

      switch (reportType) {
        case 'sales':
          const salesRequest: SalesReportRequest = {
            ...dateRange,
            group_by: groupBy,
            customer_id: customerId || undefined,
            customer_type: customerType || undefined,
            category_id: categoryId || undefined,
            include_cancelled: includeCancelled,
            format
          }
          result = await reportingService.generateSalesReport(salesRequest, true)
          break

        case 'inventory':
          const inventoryRequest: InventoryReportRequest = {
            ...dateRange,
            warehouse_id: warehouseId || undefined,
            category_id: categoryId || undefined,
            low_stock_threshold: lowStockThreshold || undefined,
            include_inactive: includeInactive,
            format
          }
          result = await reportingService.generateInventoryReport(inventoryRequest, true)
          break

        case 'pricing':
          const pricingRequest: PricingReportRequest = {
            ...dateRange,
            tier_id: tierId || undefined,
            product_id: productId || undefined,
            include_overrides: includeOverrides,
            format
          }
          result = await reportingService.generatePricingReport(pricingRequest, true)
          break

        case 'financial':
          const financialRequest: FinancialReportRequest = {
            ...dateRange,
            report_type: financialReportType,
            payment_gateway: paymentGateway || undefined,
            customer_id: customerId || undefined,
            format
          }
          result = await reportingService.generateFinancialReport(financialRequest, true)
          break
      }

      if ('job_id' in result) {
        // Background job created
        const newJob: ReportJob = {
          id: result.job_id,
          type: reportType,
          name: `${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report - ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`,
          status: result.status,
          progress: 0,
          createdAt: new Date().toISOString()
        }
        setReportJobs(prev => [newJob, ...prev])
        alert('Report generation started. You can monitor progress in the jobs list.')
      } else {
        // Immediate result - download directly
        const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${reportType}_report_${Date.now()}.json`
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('Error generating report:', error)
      alert('Error generating report. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const viewJobStatus = async (jobId: string) => {
    try {
      const status = await reportingService.getReportJobStatus(jobId)
      setJobStatus(status)
      setJobStatusDialog(jobId)
    } catch (error) {
      console.error('Error fetching job status:', error)
    }
  }

  const downloadReport = async (jobId: string) => {
    try {
      const blob = await reportingService.downloadReport(jobId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `report_${jobId}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading report:', error)
      alert('Error downloading report. Please try again.')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success'
      case 'in_progress':
        return 'primary'
      case 'pending':
        return 'warning'
      case 'failed':
        return 'error'
      default:
        return 'default'
    }
  }

  const renderReportTypeFields = () => {
    switch (reportType) {
      case 'sales':
        return (
          <>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Group By</InputLabel>
                <Select
                  value={groupBy}
                  label="Group By"
                  onChange={(e) => setGroupBy(e.target.value as any)}
                >
                  <MenuItem value="day">Daily</MenuItem>
                  <MenuItem value="week">Weekly</MenuItem>
                  <MenuItem value="month">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Customer ID (Optional)"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Customer Type (Optional)</InputLabel>
                <Select
                  value={customerType}
                  label="Customer Type (Optional)"
                  onChange={(e) => setCustomerType(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="retail">Retail</MenuItem>
                  <MenuItem value="reseller">Reseller</MenuItem>
                  <MenuItem value="wholesaler">Wholesaler</MenuItem>
                  <MenuItem value="broker">Broker</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Category ID (Optional)"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeCancelled}
                    onChange={(e) => setIncludeCancelled(e.target.checked)}
                  />
                }
                label="Include Cancelled Orders"
              />
            </Grid>
          </>
        )

      case 'inventory':
        return (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Warehouse ID (Optional)"
                value={warehouseId}
                onChange={(e) => setWarehouseId(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Low Stock Threshold (Optional)"
                type="number"
                value={lowStockThreshold}
                onChange={(e) => setLowStockThreshold(e.target.value ? parseInt(e.target.value) : '')}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)}
                  />
                }
                label="Include Inactive Products"
              />
            </Grid>
          </>
        )

      case 'pricing':
        return (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Pricing Tier ID (Optional)"
                value={tierId}
                onChange={(e) => setTierId(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Product ID (Optional)"
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeOverrides}
                    onChange={(e) => setIncludeOverrides(e.target.checked)}
                  />
                }
                label="Include Customer Price Overrides"
              />
            </Grid>
          </>
        )

      case 'financial':
        return (
          <>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Report Type</InputLabel>
                <Select
                  value={financialReportType}
                  label="Report Type"
                  onChange={(e) => setFinancialReportType(e.target.value as any)}
                >
                  <MenuItem value="unpaid_invoices">Unpaid Invoices</MenuItem>
                  <MenuItem value="payment_reconciliation">Payment Reconciliation</MenuItem>
                  <MenuItem value="revenue_analysis">Revenue Analysis</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Payment Gateway (Optional)"
                value={paymentGateway}
                onChange={(e) => setPaymentGateway(e.target.value)}
              />
            </Grid>
          </>
        )

      default:
        return null
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <PageHeader
          title="Report Generator"
          subtitle="Generate comprehensive business reports with custom filters"
        />

        <Grid container spacing={3}>
          {/* Report Configuration */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Report Configuration
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Report Type</InputLabel>
                      <Select
                        value={reportType}
                        label="Report Type"
                        onChange={(e) => setReportType(e.target.value as any)}
                      >
                        <MenuItem value="sales">Sales Report</MenuItem>
                        <MenuItem value="inventory">Inventory Report</MenuItem>
                        <MenuItem value="pricing">Pricing Report</MenuItem>
                        <MenuItem value="financial">Financial Report</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Export Format</InputLabel>
                      <Select
                        value={format}
                        label="Export Format"
                        onChange={(e) => setFormat(e.target.value as any)}
                      >
                        <MenuItem value="csv">CSV</MenuItem>
                        <MenuItem value="pdf">PDF</MenuItem>
                        <MenuItem value="json">JSON</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={setStartDate}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={setEndDate}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>

                  {renderReportTypeFields()}

                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={loading ? <CircularProgress size={20} /> : <PlayArrow />}
                      onClick={generateReport}
                      disabled={loading}
                      fullWidth
                    >
                      {loading ? 'Generating Report...' : 'Generate Report'}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Report Jobs */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Report Jobs
                </Typography>

                {reportJobs.length === 0 ? (
                  <Alert severity="info">No report jobs found</Alert>
                ) : (
                  <List>
                    {reportJobs.map((job) => (
                      <ListItem key={job.id} divider>
                        <ListItemIcon>
                          <Chip
                            label={job.type}
                            size="small"
                            color={getStatusColor(job.status) as any}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={job.name}
                          secondary={
                            <Box>
                              <Typography variant="caption" display="block">
                                Status: {job.status}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Created: {new Date(job.createdAt).toLocaleString()}
                              </Typography>
                              {job.status === 'in_progress' && (
                                <LinearProgress
                                  variant="determinate"
                                  value={job.progress}
                                  sx={{ mt: 1 }}
                                />
                              )}
                            </Box>
                          }
                        />
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => viewJobStatus(job.id)}
                          >
                            <Visibility />
                          </IconButton>
                          {job.status === 'completed' && (
                            <IconButton
                              size="small"
                              onClick={() => downloadReport(job.id)}
                            >
                              <GetApp />
                            </IconButton>
                          )}
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Job Status Dialog */}
        <Dialog
          open={jobStatusDialog !== null}
          onClose={() => setJobStatusDialog(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Report Job Status</DialogTitle>
          <DialogContent>
            {jobStatus && (
              <Box>
                <Typography variant="body1" gutterBottom>
                  <strong>Job ID:</strong> {jobStatus.job_id}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Status:</strong> {jobStatus.status}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Progress:</strong> {jobStatus.progress_percentage}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={jobStatus.progress_percentage}
                  sx={{ mb: 2 }}
                />
                <Typography variant="body1" gutterBottom>
                  <strong>Created:</strong> {new Date(jobStatus.created_at).toLocaleString()}
                </Typography>
                {jobStatus.started_at && (
                  <Typography variant="body1" gutterBottom>
                    <strong>Started:</strong> {new Date(jobStatus.started_at).toLocaleString()}
                  </Typography>
                )}
                {jobStatus.completed_at && (
                  <Typography variant="body1" gutterBottom>
                    <strong>Completed:</strong> {new Date(jobStatus.completed_at).toLocaleString()}
                  </Typography>
                )}
                {jobStatus.error_message && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {jobStatus.error_message}
                  </Alert>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setJobStatusDialog(null)}>Close</Button>
            {jobStatus?.status === 'completed' && jobStatus.download_url && (
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={() => downloadReport(jobStatus.job_id)}
              >
                Download
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  )
}

export default ReportGeneratorMUI